import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ResolveWordmark from "../components/ResolveWordmark";

function passwordStrength(pw) {
  let score = 0;
  if (pw.length >= 10) score++;
  if (/[A-Za-z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (pw.length >= 14) score++;
  return Math.min(score, 4);
}

function StrengthMeter({ pw }) {
  const score = passwordStrength(pw);
  const labels = ["", "Weak", "Fair", "Good", "Strong"];
  const colors = ["#E2E8F0", "#DC2626", "#C2410C", "#5EAB45", "#047857"];
  return (
    <div className="mt-1.5">
      <div className="flex h-1.5 gap-1">
        {[1, 2, 3, 4].map((i) => (
          <span
            key={i}
            className="flex-1 rounded-full"
            style={{
              backgroundColor: i <= score ? colors[score] : "#E2E8F0",
              transition: "background-color 0.15s ease",
            }}
          />
        ))}
      </div>
      <p
        className="mt-1 text-[11px] font-medium"
        style={{ color: score >= 1 ? colors[score] : "#94A3B8" }}
        data-testid="password-strength-label"
      >
        {pw ? labels[score] || "Very weak" : "Use ≥10 chars, with letter, number & symbol"}
      </p>
    </div>
  );
}

export default function Register() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    password: "",
    confirm: "",
    requested_role: "manager",
    reason: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  if (!loading && user) return <Navigate to="/cases" replace />;

  const update = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) {
      setError("Passwords do not match");
      toast.error("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/register", {
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        requested_role: form.requested_role,
        reason: form.reason.trim(),
      });
      setSuccess(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Registration failed — please review the form";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="relative flex min-h-screen items-center justify-center bg-brand-page px-4">
        <div className="brand-card w-full max-w-md p-8 text-center" data-testid="register-success">
          <CheckCircle2 className="mx-auto h-12 w-12" style={{ color: "#5EAB45" }} />
          <h1 className="mt-3 text-xl font-semibold text-[color:var(--page-fg)]">
            Request submitted
          </h1>
          <p className="mt-2 text-sm text-[color:var(--page-fg-secondary)]">
            We've sent your request to the HR team. You'll receive an email
            confirmation when it's reviewed.
          </p>
          <Link
            to="/login"
            className="mt-5 inline-flex items-center text-sm font-medium text-brand-navy hover:text-brand-green hover:underline"
            data-testid="register-back-to-login"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen flex-col bg-brand-page" data-testid="register-page">
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-72 navy-curve"
        aria-hidden="true"
      />
      <div className="relative z-10 mx-auto flex w-full max-w-2xl flex-1 flex-col items-center justify-center px-4 py-10">
        <div className="mb-6 flex flex-col items-center gap-2">
          <img
            src="/brand/goodwin-resolve-logo.png"
            alt="Goodwin Resolve"
            className="h-16 w-16 object-contain"
          />
          <ResolveWordmark size="lg" scheme="navy" />
        </div>
        <div className="brand-card w-full p-7 sm:p-8">
          <h1 className="text-xl font-semibold tracking-tight text-[color:var(--page-fg)]">
            Request access
          </h1>
          <p className="mt-1 text-sm text-[color:var(--page-fg-secondary)]">
            Fill out the form below. An HR admin will review your request and
            email you when your account is ready.
          </p>

          <form onSubmit={submit} className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="register-form">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="full_name" className="text-xs font-medium text-[color:var(--page-fg-secondary)]">
                Full name
              </Label>
              <Input
                id="full_name"
                required
                value={form.full_name}
                onChange={update("full_name")}
                placeholder="Jane Doe"
                className="h-11 rounded-xl border-[color:var(--border-subtle)] bg-white"
                data-testid="register-full-name"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="email" className="text-xs font-medium text-[color:var(--page-fg-secondary)]">
                Work email
              </Label>
              <Input
                id="email"
                type="email"
                required
                value={form.email}
                onChange={update("email")}
                placeholder="jane.doe@goodwinliving.org"
                className="h-11 rounded-xl border-[color:var(--border-subtle)] bg-white"
                data-testid="register-email"
              />
              <p className="text-[11px] text-[color:var(--page-fg-muted)]">
                Only @goodwinliving.org / .net / .com email addresses are
                accepted for self-service registration.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-medium text-[color:var(--page-fg-secondary)]">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                required
                value={form.password}
                onChange={update("password")}
                placeholder="≥10 chars, letter + number + symbol"
                className="h-11 rounded-xl border-[color:var(--border-subtle)] bg-white"
                data-testid="register-password"
              />
              <StrengthMeter pw={form.password} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm" className="text-xs font-medium text-[color:var(--page-fg-secondary)]">
                Confirm password
              </Label>
              <Input
                id="confirm"
                type="password"
                required
                value={form.confirm}
                onChange={update("confirm")}
                className="h-11 rounded-xl border-[color:var(--border-subtle)] bg-white"
                data-testid="register-confirm-password"
              />
              {form.confirm && form.password !== form.confirm && (
                <p className="text-[11px] text-red-600" data-testid="register-mismatch">
                  Passwords don't match
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">
                Requested role
              </Label>
              <Select
                value={form.requested_role}
                onValueChange={update("requested_role")}
              >
                <SelectTrigger className="h-11 rounded-xl border-[color:var(--border-subtle)] bg-white" data-testid="register-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="leadership">Leadership</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[color:var(--page-fg-muted)]">
                HR and Compliance roles require an admin invite.
              </p>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="reason" className="text-xs font-medium text-[color:var(--page-fg-secondary)]">
                Reason for access
              </Label>
              <Textarea
                id="reason"
                required
                rows={3}
                maxLength={500}
                value={form.reason}
                onChange={update("reason")}
                placeholder="e.g. I need to view ER cases for the team I manage."
                className="rounded-xl border-[color:var(--border-subtle)] bg-white"
                data-testid="register-reason"
              />
              <p className="text-[11px] text-[color:var(--page-fg-muted)]">
                {form.reason.length}/500
              </p>
            </div>
            {error && (
              <p
                className="sm:col-span-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                data-testid="register-error"
              >
                {error}
              </p>
            )}
            <div className="sm:col-span-2 flex items-center justify-between">
              <Link
                to="/login"
                className="text-sm font-medium text-brand-navy hover:text-brand-green hover:underline"
                data-testid="register-cancel-link"
              >
                <ArrowLeft className="mr-1 inline h-4 w-4" />
                Back to sign in
              </Link>
              <Button
                type="submit"
                disabled={submitting}
                className="h-11 rounded-xl px-6 font-semibold text-white"
                style={{ backgroundColor: "#1E3A5F" }}
                data-testid="register-submit"
                onClick={() => navigate}
              >
                {submitting ? "Submitting…" : "Request access"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
